import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { cn } from "@/lib/utils";

import { Skeleton } from "./ui/skeleton";

/**
 * A figure on a dashboard: what it counts, the number, and a note. With
 * `to`, the whole card opens the list it counts.
 */
export function StatCard({
  label,
  value,
  note,
  icon: Icon,
  to,
  loading = false,
}: {
  label: string;
  value: ReactNode;
  note?: ReactNode;
  icon?: LucideIcon;
  to?: string;
  loading?: boolean;
}) {
  const body = (
    <>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
        {Icon ? (
          <span className="rounded-md bg-primary-soft p-1.5 text-primary">
            <Icon aria-hidden="true" className="size-4" />
          </span>
        ) : null}
      </div>
      {loading ? (
        <Skeleton className="h-8 w-16" />
      ) : (
        <p className="text-2xl font-semibold tabular-nums tracking-tight sm:text-3xl">{value}</p>
      )}
      {note ? <p className="text-xs text-muted-foreground">{note}</p> : null}
    </>
  );
  const classes =
    "flex flex-col gap-2 rounded-lg border border-border bg-card p-4 shadow-sm sm:p-5";
  return to ? (
    <Link
      to={to}
      className={cn(classes, "transition-colors hover:border-primary/50 hover:bg-muted/30")}
    >
      {body}
    </Link>
  ) : (
    <div className={classes}>{body}</div>
  );
}
